import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosBackend from "../api/axiosBackend";
import OrdersFilterDialog from "../components/OrdersComponents/OrdersFilterDialog";
import OrdersSortPopover from "../components/OrdersComponents/OrdersSortPopover";
import OrderItem from "../components/OrdersComponents/OrderItem";
import { InsertOrderDialog } from "pages/InsertOrder";
import useStatuses from "hooks/useStatuses";

// shadcn
import { Card, CardContent } from "components/ui/card";

const Order = () => {
  const [orders, setOrders] = useState([]);
  const [filters, setFilters] = useState({});
  const [sortParams, setSortParams] = useState({ sortField: "", sortOrder: "" });
  const { statuses } = useStatuses();

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const queryParams = useMemo(
    () => ({ ...filters, ...sortParams }),
    [filters, sortParams]
  );

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");

    try {
      const response = await axiosBackend.get("/orders/get", {
        params: queryParams,
        withCredentials: true,
      });
      setOrders(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
      setErrorMsg("אירעה שגיאה בעת טעינת ההזמנות");
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setErrorMsg("");

      try {
        const response = await axiosBackend.get("/orders/get", {
          params: queryParams,
          withCredentials: true,
        });

        if (!alive) return;
        setOrders(response.data?.data || []);
      } catch (error) {
        if (!alive) return;
        console.error("Error fetching orders:", error);
        setErrorMsg("אירעה שגיאה בעת טעינת ההזמנות");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [queryParams]);

  const handleApplyFilters = useCallback((newFilters) => {
    setFilters(newFilters || {});
  }, []);

  const handleSortChange = useCallback((newSort) => {
    setSortParams(newSort || { sortField: "", sortOrder: "" });
  }, []);

  const handleInlineSaved = useCallback((orderId, patch) => {
    setOrders((prev) => prev.map((o) => (o._id === orderId ? { ...o, ...patch } : o)));
  }, []);

  const handleStatusChange = useCallback(async (orderId, newStatusId) => {
    // optimistic UI — store the full status object so OrderItem can render it
    const fullStatus = statuses.find((s) => s._id === newStatusId);
    setOrders((prev) =>
      prev.map((o) => (o._id === orderId ? { ...o, status: fullStatus || o.status } : o))
    );

    try {
      await axiosBackend.put(`/orders/change-status/${orderId}`, { status: newStatusId });
    } catch (error) {
      console.error("Error updating order status:", error);
      // rollback by refetching (simplest and reliable)
      fetchOrders();
    }
  }, [fetchOrders, statuses]);

  const handleCreated = useCallback((order) => {
    // prepend for better UX (newest first) - change to append if you prefer
    setOrders((prev) => [order, ...prev]);
  }, []);

  return (
    <div dir="rtl">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">הזמנות</h1>

        <div className="flex items-center gap-2">
          <OrdersFilterDialog onApply={handleApplyFilters} initialFilters={filters} statuses={statuses} />
          <OrdersSortPopover sort={sortParams} onSortChange={handleSortChange} />
          <InsertOrderDialog onCreated={handleCreated} />
        </div>
      </div>

      {/* States */}
      {loading ? (
        <Card className="mb-4">
          <CardContent className="pt-6 text-sm text-muted-foreground">טוען הזמנות…</CardContent>
        </Card>
      ) : errorMsg ? (
        <Card className="mb-4">
          <CardContent className="pt-6 text-sm text-destructive">{errorMsg}</CardContent>
        </Card>
      ) : orders.length === 0 ? (
        <Card className="mb-4">
          <CardContent className="pt-6 text-sm text-muted-foreground">
            לא נמצאו הזמנות לפי הסינון הנוכחי.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {orders.map((order) => (
            <OrderItem
              key={order._id}
              order={order}
              statuses={statuses}
              onSaved={handleInlineSaved}
              onStatusChange={handleStatusChange}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Order;
